import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { opTime } from 'utils/dates';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { OpTable } from 'new-components/DLS/OpTable/OpTable';
import { ApiEditButton } from 'new-components/DLS/OpTableCore/components/ApiEditButton';
import { tableStateKeys } from 'new-components/DLS/OpTableCore/constants/tableStateKeys';
import {
  OpTableRawColumnType,
  OpTableRecordType,
} from 'new-components/DLS/OpTableCore/types';
import { useSelectorJs } from 'utils/customHooks';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useValidateScopes } from 'utils/customHooks/useValidateScopes';
import { getOpParams } from 'utils/getOpParams';
import { heliumReceive } from 'utils/helpers';
import { useUrlSearchParams } from 'utils/customHooks/useUrlSearchParams';
import { useRemoveQueryParams } from 'utils/customHooks/useRemoveQueryParams';
import { useAddQueryParams } from 'utils/customHooks/useAddQueryParams';

import VisitorsDrawer from '../../visitors/drawer/VisitorsDrawer';
import DateRangeLocationFilter2 from '../../../components/filters/DateRangeLocationFilter2';
import {
  DATE_TIME_AM_PM_FORMAT,
  DATE_TIME_FORMAT,
} from '../../../constants/dates';
import { TABLE_HEIGHT } from '../../../constants/ui';
import {
  fetchLocations,
  LocationsState,
} from '../../../store/slices/locationsSlice';
import {
  fetchVisits,
  setSelectedVisit,
  setVisitsEndDate,
  setVisitsStartDate,
} from '../../../store/slices/visitsSlice';
import { GuestPassState } from '../../../store/slices/guestPassSlice';
import { AppDispatch, ImmutableRootState } from '../../../store/store';
import { Visit, VisitStatus } from '../../../types/visitTypes';
import { formatFullName } from '../../../utils/utils';
import { getStatusNameById } from '../../../utils/visitorsHelper';
import { useVisitsUrlFilter } from '../../../hooks/useVisitsUrlFilter';

interface GuestPassLogTableData {
  id: number;
  visitId: number;
  locationId: number;
  visitor: string;
  status: string;
  validFrom: string;
  validTo: string;
  requestedUtc: string;
  entryId: number;
  entry: string;
  result: string;
  location: string;
  passStatus: string;
  issuer: string;
}

interface GuestPassLogResponseEntry {
  id: number;
  guestPassId: number;
  entryId?: number | null;
  requestedUtc: string;
  success: number;
  guestPass: GuestPassRecord[];
}

interface GuestPassRecord {
  id: number;
  hostUserId: number;
  locationId: number;
  visitId: number;
  visitStatus: number;
  startUtc: string;
  endUtc: string;
  status: number;
  visitor: {
    firstName: string | null;
    middleName: string | null;
    lastName: string | null;
  }[];
  generatedBy?: {
    firstName: string | null;
    lastName: string | null;
  }[];
}

interface GuestPassLogEntryOption {
  value: number;
  label: string;
}

const GuestPassLogTable = () => {
  const { t } = useTranslation();

  const dispatch: AppDispatch = useDispatch();

  const { orgId } = getOpParams();
  const { userId } = useIdentityAndUser();
  const { isValid: hasAllvisitorsRead } = useValidateScopes([
    'o{orgId}-visitorAllVisitors:r',
    'o{orgId}-visitorVisitors:r',
    'o:r',
    's-o:r',
  ]);
  const { isValid: hasGuestpassRead } = useValidateScopes([
    'o{orgId}-visitorGuestPass:r',
    'o{orgId}-visitorRpt:r',
    'o{orgId}-visitorRptGuestPass:r',
    'o:r',
    's-o:r',
  ]);

  const locations = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['locations', 'locations']),
  ) as LocationsState['locations'];
  const triggerUpdate = useSelectorJs((state: ImmutableRootState) =>
    state.getIn(['guestPass', 'triggerUpdate']),
  ) as GuestPassState['triggerUpdate'];

  const [guestPassLogs, setGuestPassLogs] = useState<GuestPassLogTableData[]>(
    [],
  );
  const [entryList, setEntryList] = useState<GuestPassLogEntryOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // URL-driven location + date filter (single source of truth). Defaults to the
  // current week.
  const {
    urlLocationId,
    urlStartDate,
    urlEndDate,
    locationId: queryLocationId,
    startDate: queryStartDate,
    endDate: queryEndDate,
    startDateOnly,
    endDateOnly,
    isReady: filterReady,
    setFilter,
  } = useVisitsUrlFilter({ defaultRange: 'week' });
  const [visitStatus, setVisitStatus] = useState<VisitStatus[]>([]);
  const [statusFilterOptions, setStatusFilterOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [defaultStatusFilter, setDefaultStatusFilter] = useState<string[]>([]);

  const [isVisitorsDrawerOpen, setIsVisitorsDrawerOpen] = useState(false);
  const { drawer, visitId: visitIdString } = useUrlSearchParams();
  const removeQueryParams = useRemoveQueryParams();
  const addQueryParams = useAddQueryParams();

  const fetchGuestPassLogs = useCallback(async () => {
    if (
      entryList.length === 0 ||
      locations.length === 0 ||
      !queryStartDate ||
      !queryEndDate ||
      queryLocationId == null
    ) {
      setGuestPassLogs([]);
      setLoading(false);
      return;
    }

    try {
      // Calculate the widest UTC range when All Locations is selected
      let startUtc = queryStartDate;
      let endUtc = queryEndDate;

      if (queryLocationId === -1 && locations.length > 0) {
        // For All Locations: calculate UTC range covering all location timezones
        const utcRanges = locations.map((loc) => {
          const tz = loc.timezone || 'UTC';
          return {
            start: opTime.tz(startDateOnly, tz).startOf('day').utc().format(),
            end: opTime.tz(endDateOnly, tz).endOf('day').utc().format(),
          };
        });

        // Get the earliest start and latest end across all timezones
        startUtc = utcRanges.reduce(
          (earliest, range) =>
            range.start < earliest ? range.start : earliest,
          utcRanges[0].start,
        );
        endUtc = utcRanges.reduce(
          (latest, range) => (range.end > latest ? range.end : latest),
          utcRanges[0].end,
        );
      } else if (queryLocationId !== -1 && locations.length > 0) {
        // For Single Location: convert the location's local time to UTC
        const selectedLocation = locations.find(
          (loc) => loc.id === queryLocationId,
        );
        if (selectedLocation) {
          const tz = selectedLocation.timezone || 'UTC';
          // queryStartDate/queryEndDate are location-timezone strings, convert to UTC
          startUtc = opTime
            .tz(queryStartDate, DATE_TIME_FORMAT, tz)
            .utc()
            .format(DATE_TIME_FORMAT);
          endUtc = opTime
            .tz(queryEndDate, DATE_TIME_FORMAT, tz)
            .utc()
            .format(DATE_TIME_FORMAT);
        }
      }

      const queryParams = {
        ...(queryLocationId !== -1 && { locationId: queryLocationId }),
        startUtc,
        endUtc,
      };
      const guestPassLogResponseRaw = await heliumReceive(
        'getMsiAltaGuestPassLogByLocation',
        [orgId],
        { queryStringParams: queryParams },
      );
      const guestPassLogResponse = guestPassLogResponseRaw as {
        data?: GuestPassLogResponseEntry[] | null;
      };

      const logs: GuestPassLogTableData[] = [];
      guestPassLogResponse.data?.forEach((log) => {
        // Skip if guestPass is null or empty
        if (!log.guestPass || log.guestPass.length === 0) {
          return;
        }

        // Find the guestPass that was used in the log
        const pass = log.guestPass.find((p) => p.id === log.guestPassId);
        if (!pass) {
          return;
        }

        // Exclude records without permission or visitors
        if (
          !(hasAllvisitorsRead || userId === pass.hostUserId) ||
          !pass.visitor ||
          pass.visitor.length === 0
        ) {
          return;
        }

        const foundLocation = locations.find(
          (location) => location.id === pass.locationId,
        );
        const locationTimezone = foundLocation?.timezone || 'UTC';

        // Filter by location timezone (for both single location and "All Locations")
        const requestedInLocationTz = opTime
          .utc(log.requestedUtc)
          .tz(locationTimezone);
        const requestedDateOnly = requestedInLocationTz.format('YYYY-MM-DD');

        if (
          requestedDateOnly < startDateOnly ||
          requestedDateOnly > endDateOnly
        ) {
          return;
        }

        logs.push({
          id: log.id,
          locationId: pass.locationId,
          location: foundLocation?.name || '',
          visitId: pass.visitId,
          visitor:
            formatFullName(
              pass.visitor[0]?.firstName,
              pass.visitor[0]?.middleName,
              pass.visitor[0]?.lastName,
            ) +
            (pass.visitor.length > 1 ? ` (+${pass.visitor.length - 1})` : ''),
          status: getStatusNameById(pass.visitStatus, undefined),
          validFrom: opTime
            .utc(pass.startUtc)
            .tz(locationTimezone)
            .format(DATE_TIME_AM_PM_FORMAT),
          validTo: opTime
            .utc(pass.endUtc)
            .tz(locationTimezone)
            .format(DATE_TIME_AM_PM_FORMAT),
          passStatus: getPassStatus(pass.status, pass.startUtc, pass.endUtc),
          requestedUtc: opTime
            .utc(log.requestedUtc)
            .tz(locationTimezone)
            .format(DATE_TIME_AM_PM_FORMAT),
          entryId: log.entryId ?? 0,
          entry:
            entryList.find((entry) => entry.value === log.entryId)?.label || '',
          result: log.success === 1 ? 'Success' : 'Failed',
          issuer:
            formatFullName(
              pass.generatedBy?.[0].firstName,
              null,
              pass.generatedBy?.[0].lastName,
            ) ?? '',
        });
      });
      setGuestPassLogs(logs);
    } catch {
      // Failed to fetch guest pass logs
      setGuestPassLogs([]);
    } finally {
      setLoading(false);
    }
  }, [
    orgId,
    queryStartDate,
    queryEndDate,
    queryLocationId,
    hasAllvisitorsRead,
    entryList,
    userId,
    locations,
    startDateOnly,
    endDateOnly,
  ]);

  // Fetch visits from the URL-driven filter (race-proof). Keeps Redux dates in
  // sync so fetchVisits({ orgId }) callers stay consistent.
  useEffect(() => {
    if (
      !filterReady ||
      queryLocationId == null ||
      queryStartDate == null ||
      queryEndDate == null
    ) {
      return;
    }
    dispatch(setVisitsStartDate(queryStartDate));
    dispatch(setVisitsEndDate(queryEndDate));
    dispatch(
      fetchVisits({
        orgId,
        locationId: queryLocationId,
        startDate: queryStartDate,
        endDate: queryEndDate,
      }),
    );
    // eslint-disable-next-line
  }, [orgId, filterReady, queryLocationId, queryStartDate, queryEndDate]);

  const uniqueLocationOptions = Array.from(
    new Map(
      guestPassLogs.map((log) => [log.locationId, log.location]),
    ).entries(),
  )
    .filter(([id, name]) => id !== null && name !== null)
    .map(([id, name]) => ({ label: name!, value: id! }));

  // Fetch entry list
  useEffect(() => {
    const fetchEntryList = async () => {
      try {
        const listAllEntriesResponse = await heliumReceive(
          'getMsiAltaEntryDetails',
          [orgId],
        );
        const entries = (listAllEntriesResponse?.data || []).map(
          (entry: { id: number; name: string }) => ({
            value: entry.id,
            label: entry.name,
          }),
        );
        setEntryList(entries);
      } catch {
        // Failed to fetch entries
      }
    };

    if (orgId) {
      fetchEntryList();
    }
  }, [orgId]);

  // Fetch locations
  useEffect(() => {
    if (orgId && locations.length === 0) {
      dispatch(fetchLocations({ orgId, status: 1 }));
    }
  }, [orgId, locations, dispatch]);

  // Fetch visit statuses
  useEffect(() => {
    const fetchVisitStatuses = async () => {
      try {
        const visitorStatus = await heliumReceive('getVisitorStatus', [orgId]);
        setVisitStatus(visitorStatus?.data || []);
      } catch {
        // Failed to fetch visit statuses
      }
    };

    if (orgId) {
      fetchVisitStatuses();
    }
  }, [orgId]);

  useEffect(() => {
    if (visitStatus.length > 0) {
      const options = [
        ...visitStatus.map((status) => ({
          label: t(status.languageCode ?? '', status.name),
          value: status.name,
        })),
        {
          label: t('vst_data_noshow', 'No Show'),
          value: 'No Show',
        },
      ];

      setStatusFilterOptions(options);
      setDefaultStatusFilter(options.map((option) => option.value));
    }
  }, [visitStatus, t]);

  useEffect(() => {
    setLoading(true);
    fetchGuestPassLogs();
  }, [fetchGuestPassLogs, triggerUpdate]);

  // Table columns definition
  const columns: OpTableRawColumnType<GuestPassLogTableData>[] = [
    {
      dataIndex: 'visitor',
      label: t('guestPassLog.table.visitor'),
      filter: { type: 'input' },
      sorter: (a, b) => (a.visitor || '').localeCompare(b.visitor || ''),
    },
    {
      dataIndex: 'requestedUtc',
      label: t('guestPassLog.table.date'),
      sorter: (a, b) => opTime(b.requestedUtc).diff(a.requestedUtc),
    },
    {
      dataIndex: 'entry',
      label: t('guestPassLog.table.entry'),
      filter: {
        type: 'multiSelect',
        options: entryList,
      },
      sorter: (a, b) => a.entry.localeCompare(b.entry),
      onFilter: (value, record) => String(record.entryId) === String(value),
    },
    {
      dataIndex: 'result',
      label: t('guestPassLog.table.result'),
      filter: {
        type: 'multiSelect',
        options: [
          { label: t('guestPassLog.table.success'), value: 'Success' },
          { label: t('guestPassLog.table.failed'), value: 'Failed' },
        ],
      },
      sorter: (a, b) => a.result.localeCompare(b.result),
      defaultFilteredValue: ['Success', 'Failed'],
      render: (value) => {
        const color = value === 'Success' ? 'green' : 'var(--colorError)';
        return <span style={{ color }}>{value}</span>;
      },
    },
    {
      dataIndex: 'location',
      label: t('guestPassLog.table.location'),
      filter: {
        type: 'multiSelect',
        options: uniqueLocationOptions,
      },
      sorter: (a, b) => a.location.localeCompare(b.location),
      onFilter: (value, record) => {
        return String(record.locationId) === String(value);
      },
    },
    {
      dataIndex: 'passStatus',
      label: t('guestPassLog.table.passStatus'),
      filter: {
        type: 'multiSelect',
        options: [
          { label: t('guestPassLog.table.valid'), value: 'Valid' },
          { label: t('guestPassLog.table.invalid'), value: 'Invalid' },
        ],
      },
      defaultFilteredValue: ['Valid', 'Invalid'],
      sorter: (a, b) => a.passStatus.localeCompare(b.passStatus),
      onFilter: (value, record) => {
        return record.passStatus === value;
      },
      render: (value) => {
        const color = value === 'Valid' ? '#709A7A' : 'grey';
        const textColor = 'white';
        const translated =
          value === 'Valid'
            ? t('guestPassLog.table.valid')
            : t('guestPassLog.table.invalid');

        return (
          <div
            style={{
              backgroundColor: color,
              color: textColor,
              padding: '5px 10px',
              borderRadius: '4px',
              textAlign: 'center',
            }}
          >
            {translated}
          </div>
        );
      },
    },
  ];

  const handleDateRangeLocationFilter = (
    locationId: number,
    nextStartDate: string,
    nextEndDate: string,
  ) => {
    setFilter(locationId, nextStartDate, nextEndDate);
  };

  // Loads the visit for the drawer into Redux from the Helium API and opens it.
  const openVisitDrawer = useCallback(
    async (visitId: number) => {
      const responseRaw = await heliumReceive('describeVisit', [
        orgId,
        visitId,
      ]);
      const response = responseRaw as {
        data?: Visit[] | null;
      };
      const selectedVisit = response?.data?.[0] as Visit | undefined;
      if (selectedVisit) {
        dispatch(setSelectedVisit(selectedVisit));
        setIsVisitorsDrawerOpen(true);
      }
    },
    [orgId, dispatch],
  );

  const handleVisitClick = (visit: OpTableRecordType) => {
    const typedVisit = visit as GuestPassLogTableData;
    addQueryParams({
      drawer: 'edit',
      visitId: String(typedVisit.visitId),
    });
  };

  // Restore the visitor drawer from the URL (e.g. direct link or refresh) by
  // loading the visit from the visitId query param.
  useEffect(() => {
    if (drawer === 'edit' && visitIdString && orgId) {
      openVisitDrawer(Number(visitIdString));
    }
  }, [drawer, visitIdString, orgId, openVisitDrawer]);

  const handleDrawerClose = () => {
    setIsVisitorsDrawerOpen(false);
    dispatch(setSelectedVisit(null));
    removeQueryParams(['drawer', 'visitId']);
  };

  const getPassStatus = (
    status: number,
    startUtc: string,
    endUtc: string,
  ): string => {
    const now = opTime.utc();
    return status === 1 &&
      now.isBetween(opTime.utc(startUtc), opTime.utc(endUtc), null, '[]')
      ? 'Valid'
      : 'Invalid';
  };

  // Table properties
  const opTableProps = {
    uiStateKey: tableStateKeys.VisitorGuestPassLogMainTable,
    dataSource: guestPassLogs,
    label: t('guestPassLog.table.label'),
    columns,
    rowActions: (record: OpTableRecordType) => [
      <ApiEditButton
        key="view"
        hasWritePermission={false}
        onClick={() => handleVisitClick(record)}
      />,
    ],
    onRowClick: (record: OpTableRecordType) => handleVisitClick(record),
    height: TABLE_HEIGHT,
    allowGlobalSearch: true,
    allowExport: true,
    allowShowHideColumns: true,
    loading,
    gtm: 'activity-log-table-gtm',
    rowKey: 'id',
  };

  if (statusFilterOptions.length === 0 || defaultStatusFilter.length === 0) {
    return null;
  }

  return (
    <OpSpace orientation="vertical" size="middle" style={{ display: 'flex' }}>
      <DateRangeLocationFilter2
        key={`${urlLocationId ?? ''}-${urlStartDate ?? ''}-${urlEndDate ?? ''}`}
        onDateRangeLocationFilter={handleDateRangeLocationFilter}
        initialStartDate={startDateOnly}
        initialEndDate={endDateOnly}
      />

      <OpTable {...opTableProps} />

      {isVisitorsDrawerOpen && (
        <VisitorsDrawer
          open={isVisitorsDrawerOpen}
          onClose={handleDrawerClose}
          tab={hasGuestpassRead ? 'guestPassLog' : 'visitors'}
        />
      )}
    </OpSpace>
  );
};

export default GuestPassLogTable;
